import { Box, Flex, Grid, Heading, SystemStyleObject, Text } from '@chakra-ui/react';
import { RectangleDecoration } from '@components/Decorations';
import { KollectionType } from '@cyberkongz/api-interfaces';
import { AppLink, ArrowRightIcon, BlurIcon, LANDING_ROUTES, OpenSeaIcon } from '@cyberkongz/ui';
import { useSharedUI } from '@features/sharedUI/SharedUIProvider';
import { PropsWithChildren } from 'react';

export function KongTypeWithName({
  id,
  name,
  type,
  stylesWrapper,
}: {
  id: number;
  name: string | null;
  type: KollectionType;
  stylesWrapper?: SystemStyleObject;
}) {
  const { kongVXTheme } = useSharedUI();

  return (
    <Box color={kongVXTheme?.text} {...stylesWrapper}>
      <Heading
        variant={
          type === 'genesis'
            ? 'displaySecondary'
            : type === 'baby'
            ? 'displayPrimary'
            : 'displayTertiary'
        }
        size="sm"
        css={{
          ...(kongVXTheme?.isDark
            ? {
                color: 'textAlt',
                textShadow: 'none',
              }
            : undefined),
        }}
      >
        {type}
      </Heading>
      <Heading size="lg" css={{ mb: 8, color: 'inherit' }}>
        {name ? (
          <Box
            title={name || ''}
            css={{
              overflowY: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: [`4`, `2`],
              WebkitBoxOrient: 'vertical',
            }}
          >
            {name}
          </Box>
        ) : (
          <Box whiteSpace="nowrap">{type === 'vx' ? 'Cyberkong VX' : 'Cyberkong'}</Box>
        )}
        <Box>{`#${id}`}</Box>
      </Heading>
    </Box>
  );
}

export function KongBackButton({ type, css }: { type: KollectionType; css?: SystemStyleObject }) {
  const { kongVXTheme, history } = useSharedUI();

  return (
    <AppLink
      css={{
        fontWeight: 'medium',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 5,
        position: ['absolute', null, 'relative'],
        top: 0,
        left: 0,
        color: kongVXTheme?.text,
        zIndex: 2,
        ...css,
      }}
      href={
        history?.prev && !history.prev.includes('[')
          ? history.prev
          : `${LANDING_ROUTES.KOLLECTIONS}?kollection=${type}`
      }
    >
      <ArrowRightIcon transform="rotateZ(180deg)" />
      <Text display={['none', 'block']}>Back</Text>
    </AppLink>
  );
}

export function KongInfoWrapper({
  id,
  name,
  type,
  children,
  openseaHref,
  blurHref,
  css,
}: {
  id: number;
  name: string | null;
  type: KollectionType;
  openseaHref: string;
  blurHref: string;
  css?: SystemStyleObject;
} & PropsWithChildren) {
  const { kongVXTheme } = useSharedUI();

  return (
    <Flex
      css={{
        flexDirection: ['column'],
        gap: 10,
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 2,
        ...css,
      }}
    >
      <Box>
        <Box css={{ display: ['none', null, 'block'] }}>
          <KongBackButton type={type} />
          <KongTypeWithName {...{ id, name, type }} />
        </Box>
        <Grid gap={6} css={{ '& a': { textDecoration: 'underline' } }}>
          {children}
        </Grid>
      </Box>
      <Grid gap={6}>
        <RectangleDecoration
          stylesWrapper={{ w: 'calc(120%)' }}
          sxRectangle={{
            boxShadow: kongVXTheme?.isDark ? 'none' : '0px 0px 10px currentColor',
          }}
        />
        <Flex
          css={{
            fontWeight: 'medium',
            fontSize: 'sm',
            textTransform: 'uppercase',
            gap: 4,
            alignItems: 'center',
            '& svg': {
              w: 5,
              h: 5,
            },
          }}
        >
          view on:
          <OpenSeaIcon href={openseaHref} />
          <BlurIcon href={blurHref} />
        </Flex>
      </Grid>
    </Flex>
  );
}
